import { useEffect, useState } from "react";
import { Download, X } from "lucide-react";

const DISMISS_KEY = "afr_install_dismissed_at";
const DISMISS_TTL_MS = 7 * 24 * 60 * 60 * 1000; // remind weekly

/**
 * Native "Add to Home Screen" install prompt for Android Chrome.
 * - Listens for `beforeinstallprompt`, stashes the event, and shows a small
 *   bottom banner with Install / Dismiss buttons.
 * - Dismissals expire after 7 days so users get a gentle reminder.
 * - Auto-hides if the app is already running in standalone (installed) mode.
 */
export default function InstallPrompt() {
  const [evt, setEvt] = useState(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Already installed → never show
    if (window.matchMedia("(display-mode: standalone)").matches) return;
    if (window.navigator.standalone) return; // iOS

    const onBefore = (e) => {
      e.preventDefault();
      const dismissedAt = parseInt(localStorage.getItem(DISMISS_KEY) || "0", 10);
      if (Date.now() - dismissedAt < DISMISS_TTL_MS) return;
      setEvt(e);
      setVisible(true);
    };
    const onInstalled = () => {
      setVisible(false);
      setEvt(null);
    };
    window.addEventListener("beforeinstallprompt", onBefore);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBefore);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const install = async () => {
    if (!evt) return;
    try {
      await evt.prompt();
      const choice = await evt.userChoice;
      if (choice.outcome !== "accepted") {
        localStorage.setItem(DISMISS_KEY, String(Date.now()));
      }
    } catch {
      /* swallow — user can retry from browser menu */
    } finally {
      setVisible(false);
      setEvt(null);
    }
  };

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      data-testid="install-prompt"
      className="fixed left-3 right-3 mx-auto max-w-[460px] z-50 bottom-[88px] border border-[#3CFF7E]/40 bg-[#0A0A0A] rounded-md p-3 flex items-center gap-3 shadow-[0_0_40px_rgba(60,255,126,0.25)]"
      style={{ paddingBottom: "calc(0.75rem + env(safe-area-inset-bottom))" }}
    >
      <div className="w-10 h-10 rounded-md bg-[#3CFF7E]/10 border border-[#3CFF7E]/40 flex items-center justify-center text-[#3CFF7E] shrink-0">
        <Download size={20} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="font-display font-bold text-sm text-white">Install AutoFix Rescue</div>
        <div className="text-xs text-[#9CA3AF] mt-0.5">Faster access · works offline · push alerts</div>
      </div>
      <button
        onClick={install}
        data-testid="install-accept-btn"
        className="h-10 px-4 bg-[#3CFF7E] text-[#0A0A0A] font-display font-bold uppercase text-xs tracking-wider rounded-md"
      >
        Install
      </button>
      <button
        onClick={dismiss}
        data-testid="install-dismiss-btn"
        aria-label="Dismiss"
        className="h-10 w-10 flex items-center justify-center text-[#9CA3AF]"
      >
        <X size={18} />
      </button>
    </div>
  );
}
